import axios from "axios";
import { CalendarIcon } from "lucide-react";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  FaBuilding,
  FaUser,
  FaChartLine,
  FaMoneyBillWave,
  FaClipboardCheck,
  FaFileSignature,
  FaUpload,
  FaXmark,
} from "react-icons/fa6";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../payslip/payslippicker.css";

const steps = [
  {
    id: 1,
    title: "Company\nInformation",
    mobileTitle: "Company\nInformation",
    icon: FaBuilding,
    fields: [
      "companyName",
      "companyEmail",
      "companyPhone",
      "companyWebsite",
      "companyAddress",
      "companyLogo",
    ],
  },
  {
    id: 2,
    title: "Employee\nInformation",
    mobileTitle: "Employee\nInformation",
    icon: FaUser,
    fields: [
      "employeeName",
      "employeeId",
      "employeePhone",
      "employeeEmail",
      "residentialAddress",
      "currentDesignation",
      "currentDepartment",
      "workLocation",
      "reportingManager",
    ],
  },
  {
    id: 3,
    title: "Increment\nInformation",
    mobileTitle: "Increment\nInformation",
    icon: FaChartLine,
    fields: [
      "incrementLetterDate",
      "appraisalPeriod",
      "incrementEffectiveDate",
      "incrementType",
      "performanceRating",
      "incrementPercentage",
      "incrementAmount",
    ],
  },
  {
    id: 4,
    title: "Current &\nRevised Salary",
    mobileTitle: "Current &\nRevised Salary",
    icon: FaMoneyBillWave,
    fields: [
      "currentAnnualCtc",
      "revisedAnnualCtc",
      "currentMonthlyGrossSalary",
      "revisedMonthlyGrossSalary",
      "currentFixedSalary",
      "revisedFixedSalary",
      "currentVariablePay",
      "revisedVariablePay",
      "salaryCurrency",
      "salaryAnnexureRequired",
    ],
  },
  {
    id: 5,
    title: "Performance\nDetails",
    mobileTitle: "Performance\nDetails",
    icon: FaClipboardCheck,
    fields: [
      "performanceAssessment",
      "keyAchievements",
      "futureExpectations",
      "additionalBenefits",
      "newDesignation",
      "designationChangeIncluded",
      "additionalRemarks",
    ],
  },
  {
    id: 6,
    title: "Authorization\nDetails",
    mobileTitle: "Authorization\nDetails",
    icon: FaFileSignature,
    fields: [
      "authorizedSignatoryName",
      "authorizedSignatoryDesignation",
      "placeOfIssue",
      "referenceNumber",
      "employeeAcknowledgementRequired",
    ],
  },
];

const defaultValues = {
  // Company
  companyName: "",
  companyEmail: "",
  companyPhone: "",
  companyWebsite: "",
  companyAddress: "",
  companyLogo: null,

  // Employee
  employeeName: "",
  employeeId: "",
  employeePhone: "",
  employeeEmail: "",
  residentialAddress: "",
  currentDesignation: "",
  currentDepartment: "",
  workLocation: "",
  reportingManager: "",

  // Increment
  incrementLetterDate: "",
  appraisalPeriod: "",
  incrementEffectiveDate: "",
  incrementType: "",
  performanceRating: "",
  incrementPercentage: "",
  incrementAmount: "",

  // Salary
  currentAnnualCtc: "",
  revisedAnnualCtc: "",
  currentMonthlyGrossSalary: "",
  revisedMonthlyGrossSalary: "",
  currentFixedSalary: "",
  revisedFixedSalary: "",
  currentVariablePay: "",
  revisedVariablePay: "",
  salaryCurrency: "INR",
  salaryAnnexureRequired: "No",

  // Performance
  performanceAssessment: "",
  keyAchievements: "",
  futureExpectations: "",
  additionalBenefits: "",
  newDesignation: "",
  designationChangeIncluded: "No",
  additionalRemarks: "",

  // Authorization
  authorizedSignatoryName: "",
  authorizedSignatoryDesignation: "",
  placeOfIssue: "",
  referenceNumber: "",
  employeeAcknowledgementRequired: "No",
};

const inputClass =
  "w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-[#10324F] outline-none transition placeholder:text-slate-400 focus:border-0 focus:ring-0";

const labelClass = "mb-1.5 block text-sm font-medium text-[#10324F]";

const FormField = ({ label, required, error, children }) => (
  <div>
    <label className={labelClass}>
      {label}
      {required && <span className="ml-0.5 text-red-500">*</span>}
    </label>
    {children}
    {error && <p className="mt-1 text-[10px] text-red-500">{error.message}</p>}
  </div>
);

const BASE_URL = import.meta.env.VITE_BASE_URL;

const SalaryIncrementLetterGeneratorForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [logoPreview, setLogoPreview] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    trigger,
    setValue,
    watch,
    reset,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues,
    mode: "onBlur",
  });

  const values = watch();
  const currentStep = steps[activeStep];

  const handleNext = async () => {
    const valid = await trigger(currentStep.fields);

    if (activeStep === 0 && !logoPreview) {
      setError("companyLogo", {
        type: "required",
        message: "Company logo is required",
      });
      return;
    }

    if (!valid) return;

    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (activeStep > 0) setActiveStep((prev) => prev - 1);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setValue("companyLogo", file, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    const url = URL.createObjectURL(file);
    setLogoPreview(url);
    clearErrors("companyLogo");
  };

  const clearLogo = () => {
    setLogoPreview("");
    setValue("companyLogo", null, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onSubmit = async (data) => {
    if (isGenerating) return;

    try {
      setIsGenerating(true);

      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (key === "companyLogo") {
          if (value instanceof File) formData.append("companyLogo", value);
        } else {
          formData.append(key, value ?? "");
        }
      });

      // Keep this endpoint in sync with your backend controller.
      const response = await axios.post(
        `${BASE_URL}/generate-salaryincrementletter`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
        },
      );

      const contentType = response.headers["content-type"];

      if (!contentType?.includes("application/pdf")) {
        const text = await response.data.text();
        console.error("Backend returned:", text);
        throw new Error("Backend did not return a Salary Increment Letter PDF");
      }

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `Salary-Increment-Letter-${data.employeeName || "Employee"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.open(url, "_blank");

      reset(defaultValues);
      setLogoPreview("");
      setActiveStep(0);

      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (error) {
      console.error("Salary increment letter generation failed:", error);

      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorData = JSON.parse(text);
          alert(
            errorData?.message || "Failed to generate Salary Increment Letter",
          );
        } catch {
          alert("Failed to generate Salary Increment Letter");
        }
      } else {
        alert(error?.message || "Failed to generate Salary Increment Letter");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <section>
      <form className="w-full">
        <div className="grid grid-cols-1 gap-2 xl:grid-cols-[65%_34%]">
          <div className="min-w-0 rounded-xl border border-white p-3 shadow-sm backdrop-blur-2xl">
            {/* STEP NAVIGATION */}
            <div className="scrollbar-hide flex w-full gap-2 overflow-x-auto pb-2 sm:overflow-visible">
              {steps.map((step, index) => {
                const active = activeStep === index;

                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={async () => {
                      if (index <= activeStep) {
                        setActiveStep(index);
                        return;
                      }

                      const valid = await trigger(steps[activeStep].fields);

                      if (activeStep === 0 && !logoPreview) {
                        setError("companyLogo", {
                          type: "required",
                          message: "Company logo is required",
                        });
                        return;
                      }

                      if (valid) setActiveStep(index);
                    }}
                    className={`flex min-w-26.25 shrink-0 flex-1 items-center justify-center rounded-md px-3 py-1.5 text-center transition sm:min-w-22 ${
                      active
                        ? "bg-[#10324F] text-white shadow-sm"
                        : "border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 text-[#10324F] shadow-sm hover:bg-white/70"
                    }`}
                  >
                    <span className="whitespace-pre-line text-xs leading-4 sm:text-sm">
                      {step.title}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* CURRENT STEP */}
            <div className="mt-5">
              {activeStep === 0 && (
                <CompanyInformation
                  register={register}
                  errors={errors}
                  handleLogoChange={handleLogoChange}
                  logoPreview={logoPreview}
                  clearLogo={clearLogo}
                />
              )}

              {activeStep === 1 && (
                <EmployeeInformation register={register} errors={errors} />
              )}

              {activeStep === 2 && (
                <IncrementInformation
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}

              {activeStep === 3 && (
                <CurrentRevisedSalary register={register} errors={errors} />
              )}

              {activeStep === 4 && (
                <PerformanceDetails register={register} errors={errors} />
              )}

              {activeStep === 5 && (
                <AuthorizationDetails register={register} errors={errors} />
              )}
            </div>

            {/* NAVIGATION BUTTONS */}
            <div className="mt-7 flex items-center justify-center gap-3">
              {activeStep > 0 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="min-w-30 rounded-md border border-[#163957] px-6 py-2.5 text-sm font-medium text-[#163957] transition hover:bg-[#163957] hover:text-white"
                >
                  Previous
                </button>
              )}

              {activeStep < steps.length - 1 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="min-w-30 rounded-md bg-[#163957] px-8 py-2.5 text-sm font-medium text-white transition hover:bg-[#10324F]"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  disabled={isGenerating}
                  onClick={handleSubmit(onSubmit)}
                  className={`min-w-45 rounded-md px-8 py-2.5 text-sm font-medium text-white transition ${
                    isGenerating
                      ? "cursor-not-allowed bg-slate-400"
                      : "bg-[#10324F] hover:bg-[#10324F]"
                  }`}
                >
                  {isGenerating ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Generating...
                    </span>
                  ) : (
                    "Generate Increment Letter"
                  )}
                </button>
              )}
            </div>
          </div>

          <SalaryIncrementLetterPreview
            values={values}
            logoPreview={logoPreview}
          />
        </div>
      </form>
    </section>
  );
};

export default SalaryIncrementLetterGeneratorForm;
const CompanyInformation = ({
  register,
  errors,
  handleLogoChange,
  logoPreview,
  clearLogo,
}) => (
  <div>
    <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
      Company Information
    </h2>

    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <FormField label="Company Name" required error={errors.companyName}>
        <input
          {...register("companyName", {
            required: "Company name is required",
            minLength: { value: 2, message: "Enter a valid company name" },
          })}
          className={inputClass}
          placeholder="Enter Company name"
        />
      </FormField>

      <FormField label="Company Email" required error={errors.companyEmail}>
        <input
          type="email"
          {...register("companyEmail", {
            required: "Company email is required",
            pattern: {
              value:
                /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,3}(\.[A-Za-z]{2,3})?$/,
              message: "Enter a valid email",
            },
          })}
          className={inputClass}
          placeholder="Enter Company Email"
        />
      </FormField>

      <FormField label="Phone Number" required error={errors.companyPhone}>
        <input
          type="tel"
          inputMode="numeric"
          maxLength={10}
          {...register("companyPhone", {
            required: "Phone number is required",
            pattern: {
              value: /^[0-9]{10}$/,
              message: "Enter 10 digit phone number",
            },
          })}
          onKeyDown={numericKeyDown}
          onInput={numericInput}
          className={inputClass}
          placeholder="Enter Phone number"
        />
      </FormField>

      <FormField label="Website" error={errors.companyWebsite}>
        <input
          {...register("companyWebsite", {
            pattern: {
              value: /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/,
              message: "Enter valid website",
            },
          })}
          className={inputClass}
          placeholder="Enter Company Website"
        />
      </FormField>

      <div className="sm:col-span-2">
        <FormField
          label="Company Address"
          required
          error={errors.companyAddress}
        >
          <input
            {...register("companyAddress", {
              required: "Company address is required",
            })}
            className={inputClass}
            placeholder="Enter Company Address"
          />
        </FormField>
      </div>

      <div className="sm:col-span-2">
        <FormField label="Company Logo" required error={errors.companyLogo}>
          {logoPreview ? (
            <div className="w-60">
              <div className="relative flex h-16 w-full items-center justify-center rounded-md border border-slate-200 bg-white">
                <img
                  src={logoPreview}
                  alt="Company logo"
                  className="h-14 max-w-55 object-contain"
                />
                <button
                  type="button"
                  onClick={clearLogo}
                  className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white transition hover:bg-red-600"
                  aria-label="Remove logo"
                >
                  <FaXmark className="text-xs" />
                </button>
              </div>
            </div>
          ) : (
            <label className="flex h-16 cursor-pointer items-center justify-center rounded-md border border-dashed border-slate-300 bg-white transition hover:bg-slate-50">
              <div className="text-center text-[10px] text-[#10324F]">
                <FaUpload className="mx-auto mb-1 text-[#10324F]/30" />
                Upload Logo
              </div>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                className="hidden"
                onChange={handleLogoChange}
              />
            </label>
          )}
        </FormField>
      </div>
    </div>
  </div>
);

const EmployeeInformation = ({ register, errors }) => (
  <div>
    <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
      Employee Information
    </h2>

    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <FormField
        label="Employee Full Name"
        required
        error={errors.employeeName}
      >
        <input
          {...register("employeeName", {
            required: "Employee name is required",
          })}
          className={inputClass}
          placeholder="Enter employee name"
        />
      </FormField>

      <FormField label="Employee ID" required error={errors.employeeId}>
        <input
          {...register("employeeId", { required: "Employee ID is required" })}
          className={inputClass}
          placeholder="Enter employee ID"
        />
      </FormField>

      <FormField label="Phone Number" error={errors.employeePhone}>
        <input
          type="tel"
          inputMode="numeric"
          maxLength={10}
          {...register("employeePhone", {
            pattern: {
              value: /^[0-9]{10}$/,
              message: "Enter 10 digit phone number",
            },
          })}
          onKeyDown={numericKeyDown}
          onInput={numericInput}
          className={inputClass}
          placeholder="Enter phone number"
        />
      </FormField>

      <FormField label="Email Address" error={errors.employeeEmail}>
        <input
          type="email"
          {...register("employeeEmail", {
            pattern: {
              value:
                /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,3}(\.[A-Za-z]{2,3})?$/,
              message: "Enter a valid email",
            },
          })}
          className={inputClass}
          placeholder="Enter email address"
        />
      </FormField>

      <div className="sm:col-span-2">
        <FormField
          label="Residential Address"
          required
          error={errors.residentialAddress}
        >
          <input
            {...register("residentialAddress", {
              required: "Residential address is required",
            })}
            className={inputClass}
            placeholder="Enter address"
          />
        </FormField>
      </div>

      <FormField
        label="Current Designation"
        required
        error={errors.currentDesignation}
      >
        <input
          {...register("currentDesignation", {
            required: "Current designation is required",
          })}
          className={inputClass}
          placeholder="Enter current designation"
        />
      </FormField>

      <FormField
        label="Current Department"
        required
        error={errors.currentDepartment}
      >
        <input
          {...register("currentDepartment", {
            required: "Current department is required",
          })}
          className={inputClass}
          placeholder="Enter current department"
        />
      </FormField>

      <FormField label="Work Location" error={errors.workLocation}>
        <input
          {...register("workLocation")}
          className={inputClass}
          placeholder="Enter work location"
        />
      </FormField>

      <FormField
        label="Current Reporting Manager"
        error={errors.reportingManager}
      >
        <input
          {...register("reportingManager")}
          className={inputClass}
          placeholder="Enter reporting manager"
        />
      </FormField>
    </div>
  </div>
);

const IncrementInformation = ({ register, errors, control }) => (
  <div>
    <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
      Increment Information
    </h2>

    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <DateField
        name="incrementLetterDate"
        label="Increment letter date"
        required
        register={register}
        errors={errors}
        control={control}
      />

      <FormField
        label="Appraisal Period"
        required
        error={errors.appraisalPeriod}
      >
        <input
          {...register("appraisalPeriod", {
            required: "Appraisal period is required",
          })}
          className={inputClass}
          placeholder="e.g. 2025-2026"
        />
      </FormField>

      <DateField
        name="incrementEffectiveDate"
        label="Increment effective date"
        required
        register={register}
        errors={errors}
        control={control}
      />

      <FormField label="Increment Type" required error={errors.incrementType}>
        <select
          {...register("incrementType", {
            required: "Increment type is required",
          })}
          className={inputClass}
        >
          <option value="">Select type</option>
          <option value="Annual Increment">Annual Increment</option>
          <option value="Performance Increment">Performance Increment</option>
          <option value="Merit Increase">Merit Increase</option>
          <option value="Special Increment">Special Increment</option>
          <option value="Promotion Increment">Promotion Increment</option>
        </select>
      </FormField>

      <FormField
        label="Performance Rating"
        required
        error={errors.performanceRating}
      >
        <select
          {...register("performanceRating", {
            required: "Performance rating is required",
          })}
          className={inputClass}
        >
          <option value="">Select rating</option>
          <option value="Outstanding">Outstanding</option>
          <option value="Exceeds Expectations">Exceeds Expectations</option>
          <option value="Meets Expectations">Meets Expectations</option>
          <option value="Needs Improvement">Needs Improvement</option>
        </select>
      </FormField>

      <FormField
        label="Increment Percentage"
        required
        error={errors.incrementPercentage}
      >
        <div className="relative">
          <input
            type="number"
            min="0"
            step="0.01"
            {...register("incrementPercentage", {
              required: "Increment percentage is required",
              min: { value: 0, message: "Enter a valid percentage" },
            })}
            className={`${inputClass} pr-9`}
            placeholder="Enter percentage"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">
            %
          </span>
        </div>
      </FormField>

      <FormField
        label="Increment Amount"
        required
        error={errors.incrementAmount}
      >
        <input
          type="number"
          min="0"
          step="0.01"
          {...register("incrementAmount", {
            required: "Increment amount is required",
            min: { value: 0, message: "Enter a valid amount" },
          })}
          className={inputClass}
          placeholder="Enter increment amount"
        />
      </FormField>
    </div>
  </div>
);

const CurrentRevisedSalary = ({ register, errors }) => (
  <div>
    <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
      Current and Revised Salary
    </h2>

    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <CurrencyField
        register={register}
        name="currentAnnualCtc"
        label="Current annual CTC"
        required
        error={errors.currentAnnualCtc}
      />
      <CurrencyField
        register={register}
        name="revisedAnnualCtc"
        label="Revised annual CTC"
        required
        error={errors.revisedAnnualCtc}
      />
      <CurrencyField
        register={register}
        name="currentMonthlyGrossSalary"
        label="Current monthly gross salary"
        required
        error={errors.currentMonthlyGrossSalary}
      />
      <CurrencyField
        register={register}
        name="revisedMonthlyGrossSalary"
        label="Revised monthly gross salary"
        required
        error={errors.revisedMonthlyGrossSalary}
      />
      <CurrencyField
        register={register}
        name="currentFixedSalary"
        label="Current fixed salary"
        error={errors.currentFixedSalary}
      />
      <CurrencyField
        register={register}
        name="revisedFixedSalary"
        label="Revised fixed salary"
        error={errors.revisedFixedSalary}
      />
      <CurrencyField
        register={register}
        name="currentVariablePay"
        label="Current variable pay"
        error={errors.currentVariablePay}
      />
      <CurrencyField
        register={register}
        name="revisedVariablePay"
        label="Revised variable pay"
        error={errors.revisedVariablePay}
      />

      <FormField label="Salary Currency" required error={errors.salaryCurrency}>
        <select
          {...register("salaryCurrency", {
            required: "Salary currency is required",
          })}
          className={inputClass}
        >
          <option value="INR">INR - Indian Rupee</option>
          <option value="USD">USD - US Dollar</option>
          <option value="EUR">EUR - Euro</option>
          <option value="GBP">GBP - British Pound</option>
        </select>
      </FormField>

      <RadioField
        register={register}
        name="salaryAnnexureRequired"
        label="Salary Annexure Required"
        options={["Yes", "No"]}
        error={errors.salaryAnnexureRequired}
      />
    </div>
  </div>
);

const PerformanceDetails = ({ register, errors }) => (
  <div>
    <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
      Performance Details
    </h2>

    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <FormField
        label="Performance assessment statement"
        required
        error={errors.performanceAssessment}
      >
        <input
          {...register("performanceAssessment", {
            required: "Performance assessment is required",
          })}
          className={inputClass}
          placeholder="Enter performance assessment"
        />
      </FormField>

      <FormField
        label="Key achievements"
        required
        error={errors.keyAchievements}
      >
        <input
          {...register("keyAchievements", {
            required: "Key achievements are required",
          })}
          className={inputClass}
          placeholder="Enter key achievements"
        />
      </FormField>

      <FormField label="Future expectations" error={errors.futureExpectations}>
        <input
          {...register("futureExpectations")}
          className={inputClass}
          placeholder="Enter future expectations"
        />
      </FormField>

      <FormField
        label="Additional benefits or allowances"
        error={errors.additionalBenefits}
      >
        <input
          {...register("additionalBenefits")}
          className={inputClass}
          placeholder="Enter additional benefits or allowances"
        />
      </FormField>

      <FormField label="New designation" error={errors.newDesignation}>
        <input
          {...register("newDesignation")}
          className={inputClass}
          placeholder="Enter new designation, if applicable"
        />
      </FormField>

      <RadioField
        register={register}
        name="designationChangeIncluded"
        label="Designation change included"
        options={["Yes", "No"]}
        error={errors.designationChangeIncluded}
      />

      <div className="sm:col-span-2">
        <FormField label="Additional remarks" error={errors.additionalRemarks}>
          <textarea
            {...register("additionalRemarks")}
            rows={3}
            className={`${inputClass} resize-none`}
            placeholder="Enter additional remarks"
          />
        </FormField>
      </div>
    </div>
  </div>
);

const AuthorizationDetails = ({ register, errors }) => (
  <div>
    <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
      Authorization Details
    </h2>

    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <FormField
        label="Authorized signatory name"
        required
        error={errors.authorizedSignatoryName}
      >
        <input
          {...register("authorizedSignatoryName", {
            required: "Authorized signatory name is required",
          })}
          className={inputClass}
          placeholder="Enter signatory name"
        />
      </FormField>

      <FormField
        label="Authorized signatory designation"
        required
        error={errors.authorizedSignatoryDesignation}
      >
        <input
          {...register("authorizedSignatoryDesignation", {
            required: "Authorized signatory designation is required",
          })}
          className={inputClass}
          placeholder="Enter signatory designation"
        />
      </FormField>

      <FormField label="Place of issue" error={errors.placeOfIssue}>
        <input
          {...register("placeOfIssue")}
          className={inputClass}
          placeholder="Enter place of issue"
        />
      </FormField>

      <FormField label="Reference number" error={errors.referenceNumber}>
        <input
          {...register("referenceNumber")}
          className={inputClass}
          placeholder="Enter reference number"
        />
      </FormField>

      <div className="sm:col-span-2">
        <RadioField
          register={register}
          name="employeeAcknowledgementRequired"
          label="Employee acknowledgement required"
          options={["Yes", "No"]}
          error={errors.employeeAcknowledgementRequired}
        />
      </div>
    </div>
  </div>
);

const DateField = ({ name, label, required, errors, control }) => {
  const parseDateValue = (value) => {
    if (!value) return null;
    const [year, month, day] = String(value).split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const formatDateValue = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return (
    <FormField label={label} required={required} error={errors[name]}>
      <Controller
        name={name}
        control={control}
        rules={required ? { required: `${label} is required` } : {}}
        render={({ field }) => (
          <DatePicker
            selected={parseDateValue(field.value)}
            onChange={(date) => field.onChange(formatDateValue(date))}
            onBlur={field.onBlur}
            dateFormat="yyyy-MM-dd"
            showPopperArrow={false}
            isClearable
            placeholderText="Select date"
            showIcon
            toggleCalendarOnIconClick
            icon={<CalendarIcon className="h-4 w-4" />}
            className={`${inputClass} h-10`}
            wrapperClassName="w-full"
            popperClassName="payslip-datepicker-popper react-popper-offset"
            calendarClassName="payslip-datepicker"
            popperPlacement="top-start"
            portalId="datepicker-portal"
          />
        )}
      />
    </FormField>
  );
};

const CurrencyField = ({ register, name, label, required, error }) => (
  <FormField label={label} required={required} error={error}>
    <input
      type="number"
      min="0"
      step="0.01"
      {...register(name, {
        ...(required ? { required: `${label} is required` } : {}),
        min: { value: 0, message: "Enter a valid amount" },
      })}
      className={inputClass}
      placeholder="Enter amount"
    />
  </FormField>
);

const RadioField = ({ register, name, label, options, error }) => (
  <FormField label={label} error={error}>
    <div className="flex h-10 items-center gap-5 rounded-md border border-slate-200 bg-white px-3">
      {options.map((option) => (
        <label
          key={option}
          className="flex cursor-pointer items-center gap-1.5 text-xs text-[#10324F]"
        >
          <input
            type="radio"
            value={option}
            {...register(name)}
            className="h-3.5 w-3.5 accent-[#10324F]"
          />
          {option}
        </label>
      ))}
    </div>
  </FormField>
);

const numericKeyDown = (e) => {
  if (
    !/[0-9]/.test(e.key) &&
    !["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight"].includes(e.key)
  ) {
    e.preventDefault();
  }
};

const numericInput = (e) => {
  e.currentTarget.value = e.currentTarget.value.replace(/\D/g, "").slice(0, 10);
};

const SalaryIncrementLetterPreview = ({ values, logoPreview }) => {
  const formatDate = (date) => {
    if (!date) return "24 August, 2026";

    const [year, month, day] = String(date).split("-").map(Number);
    if (!year || !month || !day) return date;

    return new Date(year, month - 1, day).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const currency = values.salaryCurrency || "INR";

  return (
    <div className="rounded-xl border border-white p-3 shadow-sm backdrop-blur-xl">
      <h2 className="mb-5 text-sm font-semibold text-[#10324F]">Preview</h2>

      <div className="mx-auto w-full bg-white">
        <Ribbon position="top" />

        <div className="px-4 py-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="Company"
                className="h-8 max-w-25 object-contain"
              />
            ) : (
              <span className="text-lg text-[#10324F]">
                {values.companyName || "Company"}
              </span>
            )}

            {values.referenceNumber && (
              <span className="text-[9px] text-[#10324F]">
                Ref: {values.referenceNumber}
              </span>
            )}
          </div>

          {/* Heading */}
          <div className="mt-4 text-center">
            <p className="text-xs font-bold text-[#10324F]">
              SALARY INCREMENT LETTER
            </p>
          </div>

          {/* Date */}
          <div className="mt-3 text-xs text-[#10324F]">
            <p>Date: {formatDate(values.incrementLetterDate)}</p>
          </div>

          {/* Recipient */}
          <div className="mt-4 text-xs leading-4 text-[#10324F]">
            <p className="font-semibold">To:</p>
            <p>{values.employeeName || "Employee Name"}</p>
            <p>{values.currentDesignation || "Current Designation"}</p>
            <p>{values.currentDepartment || "Department"}</p>
          </div>

          {/* Body */}
          <div className="mt-3 text-xs leading-4 text-[#10324F]">
            <p>Dear {values.employeeName || "Employee Name"},</p>

            <p className="mt-3">
              We are pleased to inform you that, following the appraisal review
              for{" "}
              <strong>
                {values.appraisalPeriod || "the appraisal period"}
              </strong>
              , your performance has been reviewed and your salary has been
              revised effective from{" "}
              <strong>{formatDate(values.incrementEffectiveDate)}</strong>.
            </p>

            <p className="mt-3">
              Based on your performance and contribution to{" "}
              <strong>{values.companyName || "the organization"}</strong>, you
              have been awarded a{" "}
              <strong>{values.incrementPercentage || "—"}%</strong> salary
              increment, amounting to{" "}
              <strong>
                {currency} {values.incrementAmount || "—"}
              </strong>
              .
            </p>

            {(values.currentAnnualCtc || values.revisedAnnualCtc) && (
              <div className="mt-3 rounded-md border border-slate-200 p-3">
                <p className="mb-2 font-semibold">Salary Revision</p>
                <div className="grid grid-cols-2 gap-y-1">
                  <span>Current Annual CTC:</span>
                  <strong>
                    {currency} {values.currentAnnualCtc || "—"}
                  </strong>
                  <span>Revised Annual CTC:</span>
                  <strong>
                    {currency} {values.revisedAnnualCtc || "—"}
                  </strong>
                  <span>Current Monthly Gross:</span>
                  <strong>
                    {currency} {values.currentMonthlyGrossSalary || "—"}
                  </strong>
                  <span>Revised Monthly Gross:</span>
                  <strong>
                    {currency} {values.revisedMonthlyGrossSalary || "—"}
                  </strong>
                </div>
              </div>
            )}

            {values.performanceAssessment && (
              <p className="mt-3">
                <strong>Performance:</strong> {values.performanceAssessment}
              </p>
            )}

            {values.keyAchievements && (
              <p className="mt-3">
                <strong>Key achievements:</strong> {values.keyAchievements}
              </p>
            )}

            {values.newDesignation &&
              values.designationChangeIncluded === "Yes" && (
                <p className="mt-3">
                  Your revised designation will be{" "}
                  <strong>{values.newDesignation}</strong>.
                </p>
              )}

            {values.additionalBenefits && (
              <p className="mt-3">
                <strong>Additional benefits/allowances:</strong>{" "}
                {values.additionalBenefits}
              </p>
            )}

            {values.futureExpectations && (
              <p className="mt-3">
                We look forward to your continued contribution and expect you to
                maintain the same level of commitment and performance in the
                future.
              </p>
            )}

            {values.additionalRemarks && (
              <p className="mt-3">{values.additionalRemarks}</p>
            )}

            <p className="mt-3">
              Please note that all other terms and conditions of your employment
              remain unchanged unless communicated separately. We appreciate
              your contribution and wish you continued success with the
              organization.
            </p>
          </div>

          {/* Place/date */}
          <div className="mt-3 text-xs text-[#10324F]">
            <p>Place of Issue: {values.placeOfIssue || "__________"}</p>
            <p className="mt-1">
              Date: {formatDate(values.incrementLetterDate)}
            </p>
          </div>

          {/* Signature */}
          <div className="mt-5 text-xs text-[#10324F]">
            <p>Sincerely,</p>
            <p className="mt-3 font-semibold">
              {values.authorizedSignatoryName || "Authorised Signatory"}
            </p>
            <p>{values.authorizedSignatoryDesignation || "HR Manager"}</p>
            <p>{values.companyName || "Company Name"}</p>
          </div>

          <div className="mt-6 text-center text-xs text-[#10324F]">
            Powered by Waggex
          </div>
        </div>

        <Ribbon position="bottom" />
      </div>
    </div>
  );
};

const Ribbon = ({ position = "top" }) => {
  const isTop = position === "top";

  return (
    <div className="relative -mx-px h-9.25 w-[calc(100%+2px)] overflow-hidden">
      <div className="absolute -inset-0.5 bg-[#10324F]" />

      <div
        className="absolute -inset-0.5"
        style={{ transform: isTop ? "none" : "scaleX(-1) scaleY(-1)" }}
      >
        <div
          className="absolute inset-0 bg-[#D1DFEA]"
          style={{
            clipPath:
              "polygon(76% 0%, 100% 0%, 100% 100%, 51% 100%, 47% 60%, 70% 55%)",
          }}
        />

        <div
          className="absolute bottom-0 left-0 h-4 w-[55%] bg-white"
          style={{ clipPath: "polygon(0 0, 88% 0, 100% 100%, 0 100%)" }}
        />
      </div>
    </div>
  );
};
